import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { RefreshCw, Clock, CheckCircle2 } from 'lucide-react';
import { refreshESignStatuses } from '../api';

const STORAGE_KEY = 'offer_letters_auto_refresh_enabled';
const INTERVAL_MS = 15 * 60 * 1000; // 15 minutes
const CHUNK_SIZE = 50;
const TICK_MS = 1000; // countdown refresh

/**
 * Auto-Refresh toggle: when ON, silently polls the chunked bulk-refresh
 * endpoint every 15 minutes. Each poll iterates through all in-progress
 * offers in batches of 50 (so a single tick is always under the K8s
 * 120s ingress timeout). Surfaces a single toast per cycle if any
 * statuses changed and triggers `onChanged` so the page can refetch.
 */
export default function AutoRefreshToggle({ inProgressCount = 0, onChanged }) {
  // Start disabled; we hydrate from localStorage in a mount effect to keep
  // useState initialization pure (eslint react-hooks/purity).
  const [enabled, setEnabled] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [polling, setPolling] = useState(false);
  const [nextPollAt, setNextPollAt] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [lastResult, setLastResult] = useState(null);
  const cancelRef = useRef({ cancelled: false });

  // Hydrate from localStorage on mount
  useEffect(() => {
    try {
      setEnabled(localStorage.getItem(STORAGE_KEY) === '1');
    } catch { /* noop */ }
    setHydrated(true);
  }, []);

  // Persist on toggle (skip the initial hydration write)
  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(STORAGE_KEY, enabled ? '1' : '0');
    } catch { /* noop */ }
  }, [enabled, hydrated]);

  // Countdown ticker (only while enabled)
  useEffect(() => {
    if (!enabled) return undefined;
    const t = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(t);
  }, [enabled]);

  // Core poll routine — iterates chunks until has_more=false
  const runPoll = useCallback(async (cancelToken) => {
    setPolling(true);
    const aggregate = { total_polled: 0, changed: 0, unchanged: 0, failed: 0, signedNames: [] };
    try {
      let offset = 0;
      let total = inProgressCount || 0;
      let safety = 200;
      while (!cancelToken.cancelled && safety-- > 0) {
        const { data } = await refreshESignStatuses({ limit: CHUNK_SIZE, offset });
        if (cancelToken.cancelled) return;
        aggregate.total_polled += data.total_polled || 0;
        aggregate.changed += data.changed || 0;
        aggregate.unchanged += data.unchanged || 0;
        aggregate.failed += data.failed || 0;
        for (const r of data.results || []) {
          if (r.changed && r.new_status === 'SIGNED' && r.candidate_name) {
            aggregate.signedNames.push(r.candidate_name);
          }
        }
        if (typeof data.total_in_progress === 'number') total = data.total_in_progress;
        if (!data.has_more || (data.total_polled || 0) === 0) break;
        offset += CHUNK_SIZE;
      }
      if (cancelToken.cancelled) return;
      setLastResult({ ...aggregate, ranAt: Date.now(), total });
      if (aggregate.changed > 0) {
        const signedMsg = aggregate.signedNames.length
          ? ` — ${aggregate.signedNames.slice(0, 3).join(', ')}${aggregate.signedNames.length > 3 ? ` +${aggregate.signedNames.length - 3} more` : ''}`
          : '';
        toast.success(
          `Auto-refresh: ${aggregate.changed} status change${aggregate.changed === 1 ? '' : 's'} detected${signedMsg}`,
          { duration: 7000 },
        );
        if (onChanged) onChanged(aggregate);
      }
    } catch (e) {
      if (!cancelToken.cancelled) {
        console.warn('Auto-refresh failed:', e?.response?.data?.detail || e?.message);
        // Don't toast on background failures — too noisy.
      }
    } finally {
      if (!cancelToken.cancelled) {
        setPolling(false);
        setNextPollAt(Date.now() + INTERVAL_MS);
      }
    }
  }, [inProgressCount, onChanged]);

  // Schedule polls while enabled
  useEffect(() => {
    if (!enabled) {
      cancelRef.current.cancelled = true;
      return undefined;
    }
    const token = { cancelled: false };
    cancelRef.current = token;
    const scheduleNext = () => {
      const next = Date.now() + INTERVAL_MS;
      setNextPollAt(next);
    };
    scheduleNext();
    const interval = setInterval(() => {
      if (token.cancelled) return;
      runPoll(token).finally(() => {
        if (!token.cancelled) scheduleNext();
      });
    }, INTERVAL_MS);
    return () => {
      token.cancelled = true;
      clearInterval(interval);
    };
  }, [enabled, runPoll]);

  // Clear nextPollAt when disabled
  useEffect(() => {
    if (!enabled) setNextPollAt(null);
  }, [enabled]);

  // Cleanup on unmount
  useEffect(() => () => { cancelRef.current.cancelled = true; }, []);

  const secondsRemaining = nextPollAt ? Math.max(0, Math.round((nextPollAt - now) / 1000)) : 0;
  const mm = String(Math.floor(secondsRemaining / 60)).padStart(2, '0');
  const ss = String(secondsRemaining % 60).padStart(2, '0');

  return (
    <div
      className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 shadow-sm"
      data-testid="auto-refresh-toggle-container"
      title={
        enabled
          ? `Auto-polls Zoho every 15 minutes for ${inProgressCount} in-progress request(s). Next poll in ${mm}:${ss}.`
          : 'Turn on to silently poll Zoho every 15 minutes and surface signed/declined updates automatically.'
      }
    >
      <Switch
        checked={enabled}
        onCheckedChange={setEnabled}
        data-testid="auto-refresh-toggle-switch"
        aria-label="Toggle 15-minute auto-refresh"
      />
      <span className="text-xs font-medium text-slate-700 flex items-center gap-1.5">
        {polling ? (
          <RefreshCw className="animate-spin text-cyan-600" size={13} />
        ) : enabled ? (
          <CheckCircle2 className="text-emerald-600" size={13} />
        ) : (
          <Clock className="text-slate-400" size={13} />
        )}
        Auto-refresh
      </span>
      {enabled && (
        <span
          className="text-[10px] font-mono text-slate-500 tabular-nums"
          data-testid="auto-refresh-countdown"
        >
          {polling ? 'polling…' : `next ${mm}:${ss}`}
        </span>
      )}
      {!enabled && lastResult && (
        <span className="text-[10px] text-slate-400" data-testid="auto-refresh-last-result">
          last: {lastResult.changed} changed
        </span>
      )}
    </div>
  );
}
